import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useTenantNavigation } from "../../hooks/useTenantNavigation.js";
import {
    Button,
    Typography,
    Form,
    Input,
    Avatar,
    message,
    Card,
    Checkbox,
    Row,
    Col,
    Table,
    Tag,
    Switch,
    Spin
} from "antd";
import {
    UserOutlined,
    DownloadOutlined,
    HomeOutlined,
    UploadOutlined
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { uploadCompanyLogo, getCompanyLogoImage, getCompanyDetails, createCompany, updateCompany, deleteCompanyLogo, getAdminDashboardCounts, getAdminDashboardAnalysis, requestPlanChange } from "../../services/adminServices";
import { updateCompanyLogo, clearCompanyLogo } from "../../redux/actions/userAction";
import { setCompanyLogoUrl } from "../../redux/slices/companySlice.js";
import { Upload, Modal, Image, Flex, Select } from "antd";
import { Area } from '@ant-design/charts';
import { DownOutlined, RiseOutlined } from "@ant-design/icons";
import BreadCrumb from "../BreadCrumb";
import ManageDomainsTab from "./ManageDomainsTab";

const { Title, Text } = Typography;

const AdministratorControls = () => {
    const { t } = useTranslation();
    const navigate = useTenantNavigation();
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [form] = Form.useForm();
    const [logoUrl, setLogoUrl] = useState(null);
    const [companyLogoFile, setCompanyLogoFile] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [companyName, setCompanyName] = useState("");
    const [subscriptionDetails, setSubscriptionDetails] = useState("Standard Plan");
    const [planDetails, setPlanDetails] = useState(null); // Store the actual plan data from API
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('credit');
    const [showSubscriptionView, setShowSubscriptionView] = useState(false); // Switch between plans view and current subscription view
    const [uploading, setUploading] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingLogo, setLoadingLogo] = useState(false);
    const [chartFilter, setChartFilter] = useState('6months');
    const [chartData, setChartData] = useState([]);
    const [statsSummary, setStatsSummary] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [loadingChart, setLoadingChart] = useState(false);
    const [selectedYear, setSelectedYear] = useState(dayjs().year());
    const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
    const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(undefined);
    const [remark, setRemark] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [planChangeRequest, setPlanChangeRequest] = useState(null); // Store plan change request details {remark, request}
    // removed legacy ref usage

    const { userInfo } = useSelector(state => state.user);
    const { companyLogo } = useSelector(state => state.company);

    // Load company details and logo on mount
    useEffect(() => {
        const loadCompany = async () => {
            setLoadingLogo(true);
            try {
                // Prefer company name from userInfo
                const preferredName = userInfo?.company_name || companyName || "";
                if (!preferredName || preferredName === "null" || preferredName === "undefined") {
                    setLoadingLogo(false);
                    return;
                }
                const details = await getCompanyDetails(preferredName, userInfo?.userId);
                if (details.status === 200) {
                    const data = details.data || {};
                    const cName = data.company_name || preferredName || "";
                    setCompanyName(cName);
                    setSubscriptionDetails(data.subscription_details || "Standard Plan");

                    // Store the plan details from API response
                    if (data.plan || data.features) {
                        setPlanDetails({
                            plan: data.plan || "custom",
                            features: data.features || []
                        });
                    }

                    // Store plan change request details if they exist
                    if (data.remark || data.request) {
                        setPlanChangeRequest({
                            remark: data.remark || "",
                            request: data.request || ""
                        });
                    }

                    form.setFieldsValue({ companyName: cName, subscriptionDetails: data.subscription_details || "Standard Plan" });

                    // Check if logo is directly returned as base64 data URI (new API format)
                    if (data.logo && typeof data.logo === 'string' && data.logo.startsWith('data:')) {
                        setLogoUrl(data.logo);
                    }
                    // Fallback: check for logo as filename and fetch image (old API format)
                    else if (data.company_logo) {
                        const img = await getCompanyLogoImage(userInfo.userId, data.company_logo);
                        if (img?.content) {
                            const ext = (img.fileName || data.company_logo || "").split(".").pop()?.toLowerCase();
                            const mime = ext === "jpg" || ext === "jpeg" ? "image/jpeg" : "image/png";
                            setLogoUrl(`data:${mime};base64,${img.content}`);
                        }
                    }
                }
            } catch (error) {
                // No fallback - company logo requires a valid fileName
            } finally {
                setLoadingLogo(false);
            }
        };
        if (userInfo?.userId) loadCompany();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo?.userId]);

    // Load real dashboard data for chart
    useEffect(() => {
        const fetchChartData = async () => {
            if (activeTab !== 'dashboard') return;
            setLoadingChart(true);
            try {
                let params = {};

                if (chartFilter === 'month') {
                    params.type = 'last_month';
                } else if (chartFilter === '6months') {
                    params.type = 'last_6_month';
                } else if (chartFilter === 'year') {
                    params.type = 'last_year';
                } else if (chartFilter === 'specific-month') {
                    params.month = dayjs().month(selectedMonth - 1).format('MMMM').toUpperCase();
                    params.year = selectedYear;
                    params.type = ''; // Backend might use specific month/year params
                } else if (chartFilter === 'specific-year') {
                    params.month = '';
                    params.year = selectedYear;
                    params.type = '';
                }

                const res = await getAdminDashboardAnalysis(params);
                if (res.status === 200) {
                    const apiData = res.data?.result || res.data || [];
                    setChartData(apiData.map(item => {
                        // Extract date string from possible fields
                        const dateStr = item.month || item.day || item.label || item.date || "";
                        let formattedDate = dateStr;

                        // Format the date for better display if it's a valid date string
                        if (dateStr && dayjs(dateStr).isValid()) {
                            if (dateStr.length === 7) { // Format: YYYY-MM
                                formattedDate = dayjs(dateStr).format('MMM YY');
                            } else if (dateStr.length === 4) { // Potentially just a year
                                formattedDate = dateStr;
                            } else { // Assume it's a full date or day-specific
                                formattedDate = dayjs(dateStr).format('DD MMM');
                            }
                        }

                        return {
                            date: formattedDate,
                            value: Number(item.count ?? item.value ?? 0),
                            category: 'Requests'
                        };
                    }));
                }


            } catch (error) {
            } finally {
                setLoadingChart(false);
            }
        };

        const fetchStats = async () => {
            if (activeTab !== 'dashboard') return;
            setLoadingStats(true);
            try {
                const res = await getAdminDashboardCounts();
                if (res.status === 200) {
                    setStatsSummary(res.data?.result || res.data);
                }
            } catch (error) {
            } finally {
                setLoadingStats(false);
            }
        };

        if (activeTab === 'dashboard') {
            fetchStats();
            fetchChartData();
        }
    }, [chartFilter, selectedYear, selectedMonth, activeTab]);



    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const values = await form.validateFields();
            const payload = {
                company_logo: logoUrl || "", // Send base64 data or empty string
                company_name: values.companyName || companyName || "",
                subscription_details: values.subscriptionDetails || subscriptionDetails || "",
            };
            // choose POST or PUT based on whether company exists
            const hasExisting = !!(companyName);
            const res = hasExisting ? await updateCompany(payload, userInfo?.userId) : await createCompany(payload, userInfo?.userId);
            if (res.status === 200) {
                message.success(res.message || 'Company saved');
                // Update Redux cache so header immediately reflects the change
                if (logoUrl) {
                    dispatch(updateCompanyLogo(logoUrl));
                    dispatch(setCompanyLogoUrl(logoUrl));
                }
            } else {
                message.error(res.message || 'Failed to save');
            }
        } catch (info) {
            // validation errors already shown by antd
        } finally {
            setSaving(false);
        }
    };

    // removed unused legacy file input upload handler

    const handleAvatarClick = () => {
        if (logoUrl) setIsPreviewOpen(true);
    };

    const beforeUpload = (file) => {
        const isImage = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
        if (!isImage) message.error(t('You can only upload JPG/PNG/JPEG file'));
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) message.error(t('Image must be smaller than 2MB'));
        return isImage && isLt2M;
    };

    const getBase64 = (img, callback) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result));
        reader.readAsDataURL(img);
    };

    const customRequest = async (options) => {
        const { file, onSuccess } = options;
        setUploading(true);
        try {
            // Simply convert to base64, no API call
            setCompanyLogoFile(file);
            getBase64(file, (url) => {
                setLogoUrl(url);
                message.success(t('Image selected.'));
                onSuccess?.(file);
                setUploading(false);
            });
        } catch (e) {
            message.error(t('Failed to process image'));
            setUploading(false);
        }
    };

    const handleLogoChange = (info) => {
        const { file } = info;
        if (file.status === 'done' || file.originFileObj) {
            const fileObj = file.originFileObj || file;
            setCompanyLogoFile(fileObj);
            const reader = new FileReader();
            reader.onload = (e) => setLogoUrl(e.target.result);
            reader.readAsDataURL(fileObj);
        }
    };

    const handleRemoveCompanyLogo = async () => {
        setRemoving(true);
        try {
            // Get company details to check logo format
            const details = await getCompanyDetails(companyName || "");
            const data = details?.data || {};

            // Check if logo is stored as filename (old format) - can be deleted from server
            const fileName = data.company_logo;
            if (fileName) {
                const del = await deleteCompanyLogo(userInfo.userId, fileName);
                if (del.status === 200) {
                    message.success(t('Company logo removed from server'));
                }
            }
            // If logo is base64 data (new format), there's no file to delete on server
        } catch { }
        setCompanyLogoFile(null);
        setLogoUrl(null);
        // Clear Redux cache so header reflects the removal
        dispatch(clearCompanyLogo());
        dispatch(setCompanyLogoUrl(null)); // Clear new company slice
        setRemoving(false);
    };

    const handleChangePlanSubmit = async () => {
        if (!remark) {
            message.warning(t('Please enter a remark'));
            return;
        }
        const trimmedRemark = remark.trim();
        setSubmitting(true);
        try {
            const response = await requestPlanChange(trimmedRemark);
            if (response.status === 200) {
                setIsChangePlanModalOpen(false);
                setIsSuccessModalOpen(true);
                message.success(response.message || "Plan change request submitted successfully");

                // Update the plan change request state immediately
                setPlanChangeRequest({
                    remark: remark,
                    request: "pending"
                });
            } else {
                message.error(response.message || "Failed to submit request");
            }
        } catch (error) {
            message.error(t('Failed to submit request. Please try again.'));
        } finally {
            setSubmitting(false);
            setSelectedPlan(undefined);
            setRemark("");
        }
    };

    // tabs controlled elsewhere in header; keep for potential future use

    const renderGeneralTab = () => (

        <Form form={form} layout="vertical" initialValues={{ companyName: 'Saltoris' }}>
            <Flex gap={16} vertical>
                <div className="general-settings settings-block">
                    {/* <div className="font-semibold" style={{ margin: "0 0 10px 0", padding: 0, fontSize: "16px" }}>General</div> */}
                    {/* Company Logo Section */}
                    <Row style={{ marginBottom: '24px' }}>
                        <Col span={24}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                {loadingLogo ? (
                                    <div
                                        style={{
                                            width: 64,
                                            height: 64,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '4px',
                                            flexShrink: 0
                                        }}
                                    >
                                        <Spin />
                                    </div>
                                ) : (
                                    logoUrl ? (
                                        <div
                                            onClick={handleAvatarClick}
                                            className="cursor-pointer ring-1 ring-gray-200 hover:ring-indigo-400"
                                            style={{
                                                width: 80,
                                                height: 64,
                                                flexShrink: 0,
                                                borderRadius: '4px',
                                                overflow: 'hidden',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#f5f5f5',
                                            }}
                                        >
                                            <img
                                                src={logoUrl}
                                                alt={t('Company Logo')}
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <Avatar
                                            size={64}
                                            icon={<UserOutlined />}
                                            style={{
                                                backgroundColor: '#f56a00',
                                                flexShrink: 0
                                            }}
                                            shape="square"
                                            className="cursor-pointer ring-1 ring-gray-200 hover:ring-indigo-400"
                                            onClick={handleAvatarClick}
                                        />
                                    )
                                )}
                                <div>
                                    <Typography.Text style={{ display: 'block', marginBottom: '4px' }}>
                                        {t("Company Logo")}
                                    </Typography.Text>
                                    <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                        {t("You can use JPG, JPEG or PNG files. Max size: 2MB")}
                                    </Typography.Text>
                                    <Flex gap={8}>
                                        <Upload
                                            name="file"
                                            accept=".jpg,.jpeg,.png"
                                            showUploadList={false}
                                            beforeUpload={beforeUpload}
                                            customRequest={customRequest}
                                            onChange={handleLogoChange}
                                            disabled={uploading || removing}
                                        >
                                            <Button size="small" icon={<UploadOutlined />} loading={uploading} disabled={uploading || removing}>
                                                <span style={{ fontSize: "12px" }}>{uploading ? t("Uploading...") : t("Choose Image")}</span>
                                            </Button>
                                        </Upload>
                                        {
                                            logoUrl &&
                                            <Button size="small" type="text" danger onClick={handleRemoveCompanyLogo} loading={removing} disabled={uploading} className="!px-2">
                                                <span style={{ fontSize: "12px" }}>{t("Remove")}</span>
                                            </Button>
                                        }

                                        {uploading && (
                                            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Spin size="small" />
                                                {t("Uploading")}
                                            </Typography.Text>
                                        )}

                                    </Flex>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Form.Item
                        label={<span className="text-gray-700 font-medium">{t("Company Name")}</span>}
                        name="companyName"
                        rules={[{ required: true, whitespace: true, message: t('Please input company name!') }]}
                        className="mb-6"
                    >
                        <Input
                            size="large"
                            placeholder={t("Enter company name")}
                            className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        />
                    </Form.Item>

                    <Form.Item
                        label={<span className="text-gray-700 font-medium">{t("Subscription Details")}</span>}
                        name="subscriptionDetails"
                        initialValue="Standard Plan"
                        className="mb-6"
                    >
                        <Input
                            size="large"
                            disabled
                            className="rounded-lg bg-gray-50 border-gray-300"
                        />
                    </Form.Item>
                </div>
            </Flex>



        </Form>
    );

    const renderBillingTab = () => {
        const invoiceData = [
            {
                key: '1',
                invoiceId: 'INV-0921',
                dueDate: '10-01-2026',
                total: '₹2,299',
                status: 'Upcoming',
            },
            {
                key: '2',
                invoiceId: 'INV-1054',
                dueDate: '10-12-2025',
                total: '₹2,299',
                status: 'Paid',
            },
            {
                key: '3',
                invoiceId: 'INV-1038',
                dueDate: '10-11-2025',
                total: '₹2,299',
                status: 'Paid',
            },
            {
                key: '4',
                invoiceId: 'INV-1021',
                dueDate: '10-10-2025',
                total: '₹2,299',
                status: 'Paid',
            },
        ];

        const columns = [
            {
                title: t('Invoice ID'),
                dataIndex: 'invoiceId',
                key: 'invoiceId',
            },
            {
                title: t('Due Date'),
                dataIndex: 'dueDate',
                key: 'dueDate',
            },
            {
                title: t('Invoice Total'),
                dataIndex: 'total',
                key: 'total',
            },
            {
                title: t('Status'),
                dataIndex: 'status',
                key: 'status',
                render: (status) => (
                    <Tag
                        color={status === 'Upcoming' ? 'orange' : 'success'}
                        className="rounded-md font-medium px-2 py-0 border-none"
                        style={{
                            backgroundColor: status === 'Upcoming' ? '#FFF7E6' : '#F6FFED',
                            color: status === 'Upcoming' ? '#FAAD14' : '#52C41A'
                        }}
                    >
                        {status}
                    </Tag>
                ),
            },
            {
                title: t('Action'),
                key: 'action',
                align: 'center',
                render: () => (
                    <Button type="text" icon={<DownloadOutlined className="text-blue-600" />} />
                ),
            },
        ];

        // Helper function to get feature value by name
        const getFeatureValue = (featureName) => {
            const feature = planDetails?.features?.find(f => f.name === featureName);
            return feature?.count || 0;
        };

        // Format plan name for display
        const formatPlanName = (plan) => {
            if (!plan) return "Standard Plan";
            return plan.charAt(0).toUpperCase() + plan.slice(1) + " Plan";
        };

        // Helper function to get status tag color and text
        const getRequestStatusTag = (status) => {
            if (!status) return null;
            const statusLower = status.toLowerCase();
            let color, bgColor, text;

            if (statusLower === 'pending') {
                color = '#FAAD14';
                bgColor = '#FFF7E6';
                text = 'Pending';
            } else if (statusLower === 'approved') {
                color = '#52C41A';
                bgColor = '#F6FFED';
                text = 'Approved';
            } else if (statusLower === 'rejected') {
                color = '#FF4D4F';
                bgColor = '#FFF1F0';
                text = 'Rejected';
            } else {
                color = '#8C8C8C';
                bgColor = '#FAFAFA';
                text = status;
            }

            return (
                <Tag
                    className="rounded-md font-medium px-3 py-1 border-none"
                    style={{ backgroundColor: bgColor, color: color }}
                >
                    {text}
                </Tag>
            );
        };

        return (
            <div className="p-6 bg-gray-50/30 flex flex-col gap-6">
                {/* Billing Plan Card */}
                {/* <Card className="rounded-xl border border-gray-200 shadow-sm"> */}
                    <Flex vertical gap={12}>
                        <Text type="secondary" className="text-sm">{t("Your Billing Plan:")}</Text>
                        <Flex justify="space-between" align="center">
                            <Title level={4} className="m-0 font-bold">
                                {planDetails ? t(formatPlanName(planDetails.plan)) : t("Starter Plan")}
                            </Title>
                            <Button type="link" className="p-0 font-semibold flex items-center gap-1 text-blue-700" onClick={() => setIsChangePlanModalOpen(true)}>
                                {t("Request to Change Plan")} <span className="text-lg">→</span>
                            </Button>
                        </Flex>

                        <ul className="list-none p-0 m-0 space-y-1 text-gray-500 text-sm">
                            {planDetails ? (
                                <>
                                    <li>{t("Up-to")} {getFeatureValue('users')} {t("Users")}</li>
                                    <li>{getFeatureValue('spaces')} {t("Spaces and")} {getFeatureValue('requests')} {t("requests")}</li>
                                    <li>{t("Up-to")} {getFeatureValue('datastorage')} {t("GB Storage")}</li>
                                </>
                            ) : (
                                <>
                                    <li>{t("Up-to 10 Users")}</li>
                                    <li>{t("1 Space and 200 requests")}</li>
                                    <li>{t("Up-to 50 GB Storage")}</li>
                                    <li>{t("1 week of remote implementation support")}</li>
                                </>
                            )}
                        </ul>

                        {/* Plan Change Request Status */}
                        {planChangeRequest && (planChangeRequest.request || planChangeRequest.remark) && (
                            <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                                <Flex justify="space-between" align="start" className="mb-2">
                                    <Text strong className="text-gray-700">{t("Plan Change Request")}</Text>
                                    {planChangeRequest.request && getRequestStatusTag(planChangeRequest.request)}
                                </Flex>
                                {planChangeRequest.remark && (
                                    <div className="mt-2">
                                        <Text type="secondary" className="text-xs block mb-1">{t("Remark:")}</Text>
                                        <Text className="text-sm text-gray-600">{planChangeRequest.remark}</Text>
                                    </div>
                                )}
                            </div>
                        )}
                    </Flex>
                {/* </Card> */}

                {/* Invoices Card */}
                {/* <Card className="rounded-xl border border-gray-200 shadow-sm">
                    <Title level={4} className="mb-4">Invoices</Title>
                    <Table
                        dataSource={invoiceData}
                        columns={columns}
                        pagination={false}
                        className="billing-table"
                        rowClassName={() => "hover:bg-gray-50/50"}
                    />
                </Card> */}

                <Modal
                    title={t("Request to Change Plan")}
                    open={isChangePlanModalOpen}
                    onCancel={() => setIsChangePlanModalOpen(false)}
                    footer={null}
                    width={500}
                >
                    <Flex vertical gap={16}>
                        <Form layout="vertical" onFinish={handleChangePlanSubmit}>
                            <Form.Item
                                label={t("Remark")}
                                name="remark"
                                rules={[{ required: true, whitespace: true, message: t('Please enter your remark') }]}
                            >
                                <Input.TextArea
                                    rows={4}
                                    placeholder={t("Enter your detailed requirement for change plan ...")}
                                    value={remark}
                                    onChange={(e) => setRemark(e.target.value)}
                                />
                            </Form.Item>
                            <Flex justify="flex-end" gap={8}>
                                <Button onClick={() => setIsChangePlanModalOpen(false)}>{t("Cancel")}</Button>
                                <Button type="primary" htmlType="submit" className="success-button" loading={submitting}>
                                    {t("Submit Request")}
                                </Button>
                            </Flex>
                        </Form>
                    </Flex>
                </Modal>

                <Modal
                    title={t("Success")}
                    open={isSuccessModalOpen}
                    onCancel={() => setIsSuccessModalOpen(false)}
                    footer={[
                        <Button key="ok" type="primary" onClick={() => setIsSuccessModalOpen(false)}>
                            {t("OK")}
                        </Button>
                    ]}
                    width={400}
                >
                    <Flex vertical align="center" gap={16} style={{ padding: '20px 0' }}>
                        <div style={{ color: '#52c41a', fontSize: '48px' }}>✓</div>
                        <Text style={{ fontSize: '16px' }}>{t("Your request has been submitted")}</Text>
                    </Flex>
                </Modal>
            </div>
        );
    };

    const renderDashboardTab = () => {
        const stats = [
            {
                title: t("Total Spaces"),
                value: statsSummary?.totalSpaceCount ?? "0",
                growth: statsSummary?.spacesGrowth ?? "0 this month",
                icon: <HomeOutlined className="text-white text-xl" />,
                color: "#141bcb"
            },
            {
                title: t("Total Users"),
                value: statsSummary?.totalUserCount ?? "0",
                growth: statsSummary?.usersGrowth ?? "0 this month",
                icon: <UserOutlined className="text-white text-xl" />,
                color: "#141bcb"
            },
            {
                title: t("Total Requests"),
                value: statsSummary?.totalRequestCount ?? "0",
                growth: statsSummary?.requestsGrowth ?? "0% all time",
                icon: <RiseOutlined className="text-white text-xl" />,
                color: "#141bcb"
            }
        ];


        const config = {
            data: chartData,
            xField: 'date',
            yField: 'value',
            seriesField: 'category',

            meta: {
                date: {
                    alias: 'Date',
                },
                value: {
                    alias: 'Requests',
                },
            },
            tooltip: {
                showMarkers: false,
                title: (datum) => datum.date,
                items: [
                    {
                        channel: 'y',
                        name: 'Requests',
                        valueFormatter: (v) => v?.toLocaleString() || '0'
                    }
                ]
            },


            state: {
                active: {
                    style: {
                        shadowBlur: 4,
                        stroke: '#000',
                        fill: 'red',
                    },
                },
            },
            line: {
                style: {
                    stroke: '#0070f3',
                    lineWidth: 3,
                },
            },
            area: {
                style: {
                    fill: 'l(270) 0:#ffffff 1:#0070f3',
                    fillOpacity: 0.1,
                },
            },
            axis: {
                x: {
                    title: null,
                    labelAutoRotate: true,
                    labelAutoHide: true,
                    labelAutoEllipsis: true,
                },
                y: {
                    title: null,
                    labelFormatter: (v) => `${v}`,
                    grid: true,
                },
            },

            smooth: true,
        };

        return (
            <div className="p-6 bg-gray-50/30">
                {/* Stats Grid */}
                <Row gutter={[24, 24]} className="mb-8">
                    {stats.map((item, index) => (
                        <Col xs={24} md={8} key={index}>
                            <Card className="rounded-xl border-none shadow-sm hover:shadow-md transition-shadow">
                                <Spin spinning={loadingStats}>
                                    <Flex justify="space-between" align="start">
                                        <div>
                                            <Text type="secondary" className="text-sm font-medium mb-2 block">{item.title}</Text>
                                            <Title level={3} className="m-0 mb-1 font-bold">{item.value}</Title>
                                            {/* <Text className="text-green-500 text-xs font-semibold flex items-center gap-1">
                                                <span className="text-lg">↑</span> {item.growth}
                                            </Text> */}
                                        </div>
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                                            style={{ backgroundColor: item.color }}
                                        >
                                            {item.icon}
                                        </div>
                                    </Flex>
                                </Spin>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Chart Section */}
                <Card className="rounded-xl border-none shadow-sm p-2">
                    <Flex justify="space-between" align="center" className="mb-8 p-2">
                        <Title level={4} className="m-0">{t("Requests Growth")}</Title>
                        <Flex gap={8} align="center">
                            <Select
                                value={chartFilter}
                                style={{ width: 160 }}
                                suffixIcon={<DownOutlined />}
                                className="rounded-lg"
                                onChange={(val) => setChartFilter(val)}
                            >
                                <Select.Option value="month">{t("Last month")}</Select.Option>
                                <Select.Option value="6months">{t("Last 6 months")}</Select.Option>
                                <Select.Option value="year">{t("Last year")}</Select.Option>
                                <Select.Divider />
                                <Select.Option value="specific-month">{t("Specific month")}</Select.Option>
                                <Select.Option value="specific-year">{t("Specific year")}</Select.Option>
                            </Select>

                            {(chartFilter === 'specific-month' || chartFilter === 'specific-year') && (
                                <Select
                                    value={selectedYear}
                                    style={{ width: 100 }}
                                    className="rounded-lg"
                                    onChange={setSelectedYear}
                                >
                                    {[2023, 2024, 2025, 2026].map(y => (
                                        <Select.Option key={y} value={y}>{y}</Select.Option>
                                    ))}
                                </Select>
                            )}

                            {chartFilter === 'specific-month' && (
                                <Select
                                    value={selectedMonth}
                                    style={{ width: 130 }}
                                    className="rounded-lg"
                                    onChange={setSelectedMonth}
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                        <Select.Option key={m} value={m}>{dayjs().month(m - 1).format('MMMM')}</Select.Option>
                                    ))}
                                </Select>
                            )}
                        </Flex>
                    </Flex>
                    <Spin spinning={loadingChart}>
                        <div className="h-[350px] w-full mt-4">
                            {chartData.length > 0 ? (
                                <Area {...config} />
                            ) : (
                                <Flex align="center" justify="center" className="h-full">
                                    <Text type="secondary">{t("No data available for this period")}</Text>
                                </Flex>
                            )}
                        </div>
                    </Spin>
                </Card>
            </div>
        );

    };

    return (
        <div className="user-settings">
            <BreadCrumb items={[{ title: <HomeOutlined />, path: "/app/dashboard" }, { title: t("Administrator Controls") }]} marginb="12px" />

            <Flex align="center" justify="space-between" style={{ paddingBottom: "12px" }}>
                <Typography.Title level={4} style={{ marginTop: 0 }}>
                    {t("Administrator Controls")}
                </Typography.Title>
                {activeTab === 'general' && (
                    <Flex gap={8}>
                        <Button className="success-button" onClick={handleSave} loading={saving} disabled={saving || uploading || removing}>
                            {t("Save")}
                        </Button>
                    </Flex>
                )}
            </Flex>
            {/* Tab Navigation */}
            <div className="mb-6">
                <div className="bg-gray-100 rounded-lg p-1 inline-flex w-full sm:w-auto">
                    <button
                        className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === "dashboard"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                        onClick={() => setActiveTab("dashboard")}
                    >
                        {t("Dashboard")}
                    </button>
                    <button
                        className={`flex-1 sm:flex-none px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === "domains"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                        onClick={() => setActiveTab("domains")}
                    >
                        {t("Manage Domain")}
                    </button>
                    <button
                        className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === "billing"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                        onClick={() => setActiveTab("billing")}
                    >
                        {t("Billing")}
                    </button>
                    <button
                        className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === "general"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                        onClick={() => setActiveTab("general")}
                    >
                        {t("General Setting")}
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                {activeTab === 'dashboard' && renderDashboardTab()}
                {activeTab === 'domains' && <ManageDomainsTab />}
                {activeTab === 'billing' && renderBillingTab()}
                {activeTab === 'general' && renderGeneralTab()}
            </div>

            <Modal
                title={t("Company Logo")}
                open={isPreviewOpen}
                footer={null}
                onCancel={() => setIsPreviewOpen(false)}
                width={420}
            >
                {logoUrl ? (
                    <Image src={logoUrl} width={360} />
                ) : (
                    <Text type="secondary">{t("No image")}</Text>
                )}
            </Modal>
        </div>
    );

    // removed unreachable duplicate component JSX
};

export default AdministratorControls;
